import { createSlice } from '@reduxjs/toolkit';

// A mega menu column spans `gridUnits` out of 12 (see NavigationColumn in
// types.js). The value flows unvalidated from the nav API into a CSS
// grid-template-columns string, so a malformed value would produce an invisible
// or broken column. Clamp it once here, at the boundary where nav data enters
// the store, so every downstream reader (MegaMenu, MegaMenuColumn) can trust it.
const MIN_GRID_UNITS = 1;
const MAX_GRID_UNITS = 12;
const DEFAULT_GRID_UNITS = 1;

/**
 * Coerce a raw `gridUnits` value to a valid integer in the documented 1–12
 * range. Missing, non-numeric, zero, negative, or out-of-range values fall back
 * to DEFAULT_GRID_UNITS; values above the max are clamped down.
 *
 * @param {unknown} rawGridUnits
 * @returns {number}
 */
const clampGridUnits = (rawGridUnits) => {
	const parsed = Math.trunc(Number(rawGridUnits));
	if (!Number.isFinite(parsed) || parsed < MIN_GRID_UNITS) {
		return DEFAULT_GRID_UNITS;
	}
	return Math.min(parsed, MAX_GRID_UNITS);
};

/**
 * Return a copy of a column list with every column's `gridUnits` validated.
 *
 * @param {NavigationColumn[]} columns
 * @returns {NavigationColumn[]}
 */
const normalizeColumns = (columns) =>
	columns.map((column) => ({
		...column,
		gridUnits: clampGridUnits(column.gridUnits)
	}));

/**
 * Return a copy of the nav items with every column's `gridUnits` validated.
 * Items without a `columns` array are returned untouched.
 *
 * @param {NavigationItem[]} items
 * @returns {NavigationItem[]}
 */
const normalizeGridUnits = (items) =>
	items.map((item) => {
		if (!Array.isArray(item.columns)) return item;
		return { ...item, columns: normalizeColumns(item.columns) };
	});

const navDataSlice = createSlice({
	name: 'navData',
	initialState: {
		items: []
	},
	reducers: {
		/* eslint-disable no-param-reassign --
		   RTK's createSlice uses Immer internally, so these "mutations"
		   produce immutable updates. Direct assignment is the intended API. */
		setItems(state, action) {
			state.items = normalizeGridUnits(action.payload ?? []);
		}
		/* eslint-enable no-param-reassign */
	}
});

export const { setItems } = navDataSlice.actions;
export default navDataSlice;

/**
 * Returns the subset of top-level navigation items that have featured === true,
 * in their original order.
 * @param {Object} state - Redux root state
 * @returns {NavigationItem[]}
 */
export const selectFeaturedItems = (state) =>
	(state.navData?.items ?? []).filter((item) => item.featured);
